import { XIcon } from "lucide-react";

import { Center, ErrorText, LoadingIndicator } from "@/components";
import { CandidateSheetV2 } from "@jobs/candidate-sheet-v2/CandidateSheetV2";
import { Sheet, SheetClose, SheetContent } from "@/components/ui/sheet";
import { SIDEBAR_WIDTH, useSidebar } from "@/components/ui/sidebar";

import { SheetScopeProvider } from "@/contexts/SheetScopeContext";
import { useGetJob } from "@/hooks/queries/useGetJob";
import { GetJobResponse } from "@/types";

// Fetches the Job and provides id-only sheet scope so the candidate sheet's
// queries resolve outside the /companies/:companyId/jobs/:jobId route.
export const MyAviaryCandidateSheet = ({
  candidacyId,
  canMoveToNextCandidacy,
  canMoveToPreviousCandidacy,
  companyId,
  currentCandidacyIndex,
  handleMoveToNextCandidacy,
  handleMoveToPreviousCandidacy,
  jobId,
  setSelectedCandidacyId,
  totalCandidaciesCount,
  userId,
}: {
  candidacyId: string;
  canMoveToNextCandidacy: boolean;
  canMoveToPreviousCandidacy: boolean;
  companyId: string;
  currentCandidacyIndex: number;
  handleMoveToNextCandidacy: () => void;
  handleMoveToPreviousCandidacy: () => void;
  jobId: string;
  setSelectedCandidacyId: (candidacyId: string | null) => void;
  totalCandidaciesCount: number;
  userId: number;
}) => {
  /* Database CRUD Operations */
  // Read
  const {
    data: job,
    isError,
    isPending,
  } = useGetJob({
    companyId,
    jobId,
    // Keep previous job visible so cross-job nav doesn't flash a loader.
    placeholderData: (prev: GetJobResponse | undefined) => prev,
  });

  /* React Hooks */
  const { open: sidebarOpen } = useSidebar();

  /* Handlers */
  const handleClose = () => {
    setSelectedCandidacyId(null);
  };

  /* Render */
  // The candidate sheet owns its own frame once the job resolves; this shell
  // only covers the job fetch's loading and error states.
  const renderShell = (contents: React.ReactNode) => (
    <Sheet onOpenChange={handleClose} open>
      <SheetContent
        className="flex !min-w-[1000px] flex-col overflow-hidden p-0"
        style={{
          maxWidth: `calc(100% - ${sidebarOpen ? SIDEBAR_WIDTH : "48px"})`,
          width: `calc(100% - ${sidebarOpen ? SIDEBAR_WIDTH : "48px"})`,
        }}
        hideClose
        stopWheelPropagation
      >
        <SheetClose className="px-6 pt-4" tabIndex={-1}>
          <XIcon className="h-4 w-4 text-slate-700" />
        </SheetClose>
        {contents}
      </SheetContent>
    </Sheet>
  );

  const render = () => {
    if (isError) {
      return renderShell(
        <Center className="flex-1">
          <ErrorText errorMessage="Error occurred when loading the job." />
        </Center>,
      );
    }
    if (isPending) {
      return renderShell(
        <Center className="flex-1">
          <LoadingIndicator variant="large" />
        </Center>,
      );
    }
    return (
      <SheetScopeProvider
        candidacyId={candidacyId}
        companyId={companyId}
        jobId={jobId}
      >
        <CandidateSheetV2
          candidacyId={candidacyId}
          canMoveToNextCandidacy={canMoveToNextCandidacy}
          canMoveToPreviousCandidacy={canMoveToPreviousCandidacy}
          currentCandidacyIndex={currentCandidacyIndex}
          handleMoveToNextCandidacy={handleMoveToNextCandidacy}
          handleMoveToPreviousCandidacy={handleMoveToPreviousCandidacy}
          job={job}
          onClose={handleClose}
          totalCandidaciesCount={totalCandidaciesCount}
          workerId={userId}
        />
      </SheetScopeProvider>
    );
  };

  return render();
};
